import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronRight, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../../../ui/card';
import { Button } from '../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import {
  WEEKDAY_OPTIONS,
  buildWeeklySeriesPreview,
  createWeeklySeriesForm,
  getNextWeekdayDate,
  parseCustomSkipDates,
} from './bulkEventGenerator';
import {
  buildEventDraft,
  createLocalEvent,
  draftToEvent,
  formatEventSummary,
  getEventStatusMeta,
  getUpcomingEventIssues,
  hasEventEnded,
  sortUpcomingEvents,
} from './upcomingEvents';

const SECTION_KEY = 'upcoming-events';

const UpcomingEventsSection = ({
  settings,
  setSettings,
  openSections,
  setOpenSections,
  styles,
  targetEventId = null,
  targetRequestKey = null,
}) => {
  const [eventEditorOpen, setEventEditorOpen] = useState(false);
  const [eventDraft, setEventDraft] = useState(() => buildEventDraft());
  const [eventPendingDelete, setEventPendingDelete] = useState(null);
  const [highlightedEventId, setHighlightedEventId] = useState(null);
  const [pastOpen, setPastOpen] = useState(false);
  const [seriesOpen, setSeriesOpen] = useState(false);
  const [seriesForm, setSeriesForm] = useState(() => createWeeklySeriesForm());
  const [seriesPreview, setSeriesPreview] = useState(null);
  const [seriesIssues, setSeriesIssues] = useState([]);
  const [seriesStartAutomatic, setSeriesStartAutomatic] = useState(true);
  const [seriesEndAutomatic, setSeriesEndAutomatic] = useState(true);
  const eventRowRefs = useRef({});

  const allSiteEvents = useMemo(
    () => (Array.isArray(settings.upcoming_events) ? settings.upcoming_events : []),
    [settings.upcoming_events]
  );
  // Soonest upcoming event first; past events go in their own collapsed section at the bottom.
  const { upcomingEvents, pastEvents } = useMemo(() => {
    const sorted = sortUpcomingEvents(allSiteEvents);
    return {
      upcomingEvents: sorted.filter((event) => !hasEventEnded(event)),
      pastEvents: sorted.filter((event) => hasEventEnded(event)),
    };
  }, [allSiteEvents]);
  const eventStatusCounts = useMemo(
    () =>
      allSiteEvents.reduce((counts, event) => {
        if (hasEventEnded(event)) {
          counts.past += 1;
        } else {
          counts.upcoming += 1;
        }
        return counts;
      }, { upcoming: 0, past: 0 }),
    [allSiteEvents]
  );
  const eventIssueCounts = useMemo(
    () =>
      allSiteEvents.reduce((counts, event) => {
        if (getUpcomingEventIssues(event).length > 0) {
          counts.needsAttention += 1;
        }
        return counts;
      }, { needsAttention: 0 }),
    [allSiteEvents]
  );
  const eventDraftIssues = useMemo(
    () => getUpcomingEventIssues(draftToEvent(eventDraft)),
    [eventDraft]
  );
  const sectionOpen = openSections.includes(SECTION_KEY);
  const eventExists = allSiteEvents.some((event) => event.event_id === eventDraft.event_id);

  useEffect(() => {
    if (highlightedEventId && pastEvents.some((event) => event.event_id === highlightedEventId)) {
      setPastOpen(true);
    }
  }, [highlightedEventId, pastEvents]);

  useEffect(() => {
    if (!highlightedEventId) return undefined;
    const frame = window.requestAnimationFrame(() => {
      eventRowRefs.current[highlightedEventId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    const timeout = window.setTimeout(() => setHighlightedEventId(null), 1800);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [highlightedEventId, allSiteEvents]);

  useEffect(() => {
    if (!targetEventId || !targetRequestKey) return;
    setOpenSections((current) => (
      current.includes(SECTION_KEY)
        ? current
        : [...current, SECTION_KEY]
    ));
    setHighlightedEventId(targetEventId);
  }, [setOpenSections, targetEventId, targetRequestKey]);

  const toggleSectionOpen = () => {
    setOpenSections((current) => (
      current.includes(SECTION_KEY)
        ? current.filter((item) => item !== SECTION_KEY)
        : [...current, SECTION_KEY]
    ));
  };

  const openEventEditor = (event = createLocalEvent()) => {
    setEventDraft(buildEventDraft(event));
    setEventEditorOpen(true);
  };

  const closeEventEditor = () => {
    setEventEditorOpen(false);
    setEventDraft(buildEventDraft());
  };

  const confirmRemoveUpcomingEvent = () => {
    if (!eventPendingDelete?.event_id) return;
    setSettings({
      ...settings,
      upcoming_events: sortUpcomingEvents(
        allSiteEvents.filter((event) => event.event_id !== eventPendingDelete.event_id)
      ),
    });
    toast.success('Event removed from draft settings. Click Save Settings to publish it.');
    setEventPendingDelete(null);
  };

  const saveEventDraft = () => {
    const nextEvent = draftToEvent(eventDraft);
    const issues = getUpcomingEventIssues(nextEvent);

    if (issues.length > 0) {
      toast.error(issues[0]);
      return;
    }

    const nextEvents = sortUpcomingEvents(
      eventExists
        ? allSiteEvents.map((event) => (event.event_id === nextEvent.event_id ? nextEvent : event))
        : [...allSiteEvents, nextEvent]
    );

    setSettings({
      ...settings,
      upcoming_events: nextEvents,
    });
    setHighlightedEventId(nextEvent.event_id);
    setEventEditorOpen(false);
    toast.success(
      eventExists
        ? 'Event updated in draft settings. Click Save Settings to publish it.'
        : 'Event added to draft settings. Click Save Settings to publish it.'
    );
  };

  const resetSeriesDialog = () => {
    setSeriesForm(createWeeklySeriesForm());
    setSeriesPreview(null);
    setSeriesIssues([]);
    setSeriesStartAutomatic(true);
    setSeriesEndAutomatic(true);
  };

  const openSeriesDialog = () => {
    resetSeriesDialog();
    setSeriesOpen(true);
  };

  const closeSeriesDialog = () => {
    setSeriesOpen(false);
    resetSeriesDialog();
  };

  const updateSeriesField = (field, value) => {
    setSeriesPreview(null);
    setSeriesIssues([]);
    setSeriesForm((current) => ({ ...current, [field]: value }));
  };

  const updateSeriesDay = (value) => {
    const dayOfWeek = Number(value);
    const nextStart = getNextWeekdayDate(dayOfWeek);
    setSeriesPreview(null);
    setSeriesIssues([]);
    setSeriesForm((current) => ({
      ...current,
      dayOfWeek,
      ...(seriesStartAutomatic ? { startDate: nextStart } : {}),
      ...(seriesEndAutomatic && nextStart > '2027-12-31' ? { endDate: nextStart } : {}),
    }));
  };

  const previewWeeklySeries = () => {
    const parsedCustomDates = parseCustomSkipDates(seriesForm.customSkipText);
    if (parsedCustomDates.invalidValues.length > 0) {
      setSeriesIssues([
        `Use YYYY-MM-DD for custom skip dates: ${parsedCustomDates.invalidValues.join(', ')}`,
      ]);
      setSeriesPreview(null);
      return;
    }

    try {
      const preview = buildWeeklySeriesPreview({
        ...seriesForm,
        customSkipDates: parsedCustomDates.dates,
      });
      if (preview.issues.length > 0) {
        setSeriesIssues(preview.issues.map((item) => item.message));
        setSeriesPreview(null);
        return;
      }
      setSeriesIssues([]);
      setSeriesPreview(preview);
    } catch (_) {
      setSeriesIssues(['Unable to preview this weekly series. Check the details and try again.']);
      setSeriesPreview(null);
    }
  };

  const confirmWeeklySeries = () => {
    if (!seriesPreview?.events?.length) return;
    const nextEvents = sortUpcomingEvents([...allSiteEvents, ...seriesPreview.events]);
    setSettings({ ...settings, upcoming_events: nextEvents });
    setHighlightedEventId(seriesPreview.events[0].event_id);
    closeSeriesDialog();
    toast.success('Events added to draft settings. Click Save Settings to publish them.');
  };

  const renderEventRow = (event, index) => {
      const status = getEventStatusMeta(event);
      const eventIssues = getUpcomingEventIssues(event);
      return (
        <div
          key={event.event_id || index}
          ref={(node) => {
            eventRowRefs.current[event.event_id] = node;
          }}
          className={`${styles.panel} p-4 ${highlightedEventId === event.event_id ? 'ring-2 ring-aqua/40' : ''}`}
        >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className={`${styles.sectionTitle} text-base`}>
                      {event.title?.trim() || `Event ${index + 1}`}
                    </p>
                    <span className={`${styles.statusPill} ${status.className}`}>
                      {status.label}
                    </span>
                    {event.location_label ? <span className={styles.mutedPillTight}>{event.location_label}</span> : null}
                    {event.link ? <span className={styles.mutedPillTight}>Link ready</span> : null}
                    {event.end_datetime ? <span className={styles.mutedPillTight}>Has end time</span> : null}
                  </div>
                  <p className={`${styles.subtleText} mt-1`}>{formatEventSummary(event)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => openEventEditor(event)}
                    className={styles.outlineButton}
                    size="icon"
                    aria-label={`Edit ${event.title?.trim() || `Event ${index + 1}`}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEventPendingDelete(event)}
                    className={styles.outlineButton}
                    size="icon"
                    aria-label={`Delete ${event.title?.trim() || `Event ${index + 1}`}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {eventIssues.length > 0 ? (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 dark:border-red-400/20 dark:bg-red-500/5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-700 dark:text-red-300">
                    Save blockers
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-red-700 dark:text-red-200">
                    {eventIssues.map((issue) => (
                      <p key={issue}>{issue}</p>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      );
  };

  return (
    <>
      <Card className={`${styles.card} p-6`}>
        <button
          type="button"
          onClick={toggleSectionOpen}
          className="flex w-full items-start gap-3 text-left"
        >
          <span className={styles.disclosureIcon}>
            <ChevronRight className={`h-4 w-4 transition-transform ${sectionOpen ? 'rotate-90' : ''}`} />
          </span>
          <div className="min-w-0 flex-1 max-w-3xl">
            <h3 className={`${styles.sectionTitle} flex items-center gap-2`}>
              <Calendar className="w-5 h-5 text-aqua" />
              Upcoming Events
            </h3>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className={styles.neutralBadge}>{eventStatusCounts.upcoming} upcoming</span>
              <span className={styles.neutralBadge}>{eventStatusCounts.past} past</span>
              <span className={styles.neutralBadge}>{eventIssueCounts.needsAttention} need attention</span>
            </div>
          </div>
        </button>

        {sectionOpen ? (
          <>
            <div className="mb-4 mt-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => openEventEditor()} className="bg-aqua text-charcoal hover:bg-aqua-dark">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Event
                </Button>
                <Button type="button" variant="outline" onClick={openSeriesDialog} className={styles.outlineButton}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Weekly Series
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              {upcomingEvents.length === 0 ? (
                <div className={`${styles.panel} p-4`}>
                  <p className={styles.bodyText}>No upcoming events yet. Add one here and it will appear anywhere Dad Partners shows upcoming events.</p>
                </div>
              ) : (
                upcomingEvents.map(renderEventRow)
              )}

              {pastEvents.length > 0 ? (
                <div className={`${styles.panel} p-4`}>
                  <button
                    type="button"
                    onClick={() => setPastOpen((open) => !open)}
                    aria-expanded={pastOpen}
                    className="flex w-full items-center justify-between gap-3 text-left"
                  >
                    <span className={`${styles.sectionTitle} text-base`}>Past events ({pastEvents.length})</span>
                    <ChevronRight className={`h-4 w-4 transition-transform ${pastOpen ? 'rotate-90' : ''}`} />
                  </button>
                  {pastOpen ? (
                    <div className="mt-4 space-y-4">{pastEvents.map(renderEventRow)}</div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </Card>

      <Dialog open={seriesOpen} onOpenChange={(open) => (open ? setSeriesOpen(true) : closeSeriesDialog())}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{seriesPreview ? 'Preview weekly series' : 'Generate weekly series'}</DialogTitle>
            <DialogDescription>
              {seriesPreview
                ? 'Review every included date before adding these events to the Site Settings draft.'
                : 'Set the weekly schedule and exclusions. Nothing is published until you click Save Settings.'}
            </DialogDescription>
          </DialogHeader>

          {seriesPreview ? (
            <div className="space-y-4">
              <div className={`${styles.panel} p-4`}>
                <p className={styles.sectionTitle}>{seriesPreview.events.length} events will be added</p>
                <p className={`${styles.subtleText} mt-1`}>
                  {seriesPreview.summary.skippedDates.length} dates skipped in total
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={styles.neutralBadge}>
                    {seriesPreview.summary.skippedCounts.fifthOccurrence} fifth occurrences
                  </span>
                  <span className={styles.neutralBadge}>
                    {seriesPreview.summary.skippedCounts.holiday} listed holidays
                  </span>
                  <span className={styles.neutralBadge}>
                    {seriesPreview.summary.skippedCounts.mothersDay} Mother's Day
                  </span>
                  <span className={styles.neutralBadge}>
                    {seriesPreview.summary.skippedCounts.fathersDay} Father's Day
                  </span>
                  <span className={styles.neutralBadge}>
                    {seriesPreview.summary.skippedCounts.custom} custom dates
                  </span>
                </div>
              </div>
              <div className={`${styles.panel} max-h-64 overflow-y-auto p-4`}>
                <div className="space-y-2">
                  {seriesPreview.summary.includedDates.map((dateValue) => (
                    <p key={dateValue} className={styles.bodyText}>
                      {new Date(`${dateValue}T12:00:00`).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label htmlFor="weekly-series-title" className={`${styles.label} mb-1 block`}>Title pattern</label>
                <Input
                  id="weekly-series-title"
                  aria-label="Title pattern"
                  value={seriesForm.titlePattern}
                  onChange={(event) => updateSeriesField('titlePattern', event.target.value)}
                  className={styles.input}
                  placeholder="Dad Partners Sunday Call #{n}"
                />
              </div>
              <div>
                <label htmlFor="weekly-series-day" className={`${styles.label} mb-1 block`}>Day of week</label>
                <select
                  id="weekly-series-day"
                  aria-label="Day of week"
                  value={seriesForm.dayOfWeek}
                  onChange={(event) => updateSeriesDay(event.target.value)}
                  className={styles.select}
                >
                  {WEEKDAY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="weekly-series-start" className={`${styles.label} mb-1 block`}>Start date</label>
                <Input
                  id="weekly-series-start"
                  type="date"
                  aria-label="Start date"
                  value={seriesForm.startDate}
                  onChange={(event) => {
                    setSeriesStartAutomatic(false);
                    updateSeriesField('startDate', event.target.value);
                  }}
                  className={styles.dateInput || styles.input}
                />
              </div>
              <div>
                <label htmlFor="weekly-series-end" className={`${styles.label} mb-1 block`}>End date</label>
                <Input
                  id="weekly-series-end"
                  type="date"
                  aria-label="End date"
                  value={seriesForm.endDate}
                  onChange={(event) => {
                    setSeriesEndAutomatic(false);
                    updateSeriesField('endDate', event.target.value);
                  }}
                  className={styles.dateInput || styles.input}
                />
              </div>
              <div>
                <label htmlFor="weekly-series-start-time" className={`${styles.label} mb-1 block`}>Start time</label>
                <Input
                  id="weekly-series-start-time"
                  type="time"
                  aria-label="Start time"
                  value={seriesForm.startTime}
                  onChange={(event) => updateSeriesField('startTime', event.target.value)}
                  className={styles.input}
                />
              </div>
              <div>
                <label htmlFor="weekly-series-end-time" className={`${styles.label} mb-1 block`}>End time</label>
                <Input
                  id="weekly-series-end-time"
                  type="time"
                  aria-label="End time"
                  value={seriesForm.endTime}
                  onChange={(event) => updateSeriesField('endTime', event.target.value)}
                  className={styles.input}
                />
                <p className={`${styles.subtleText} mt-2`}>Optional. Leave blank for a start time only.</p>
              </div>
              <div>
                <label htmlFor="weekly-series-location" className={`${styles.label} mb-1 block`}>Location label</label>
                <Input
                  id="weekly-series-location"
                  aria-label="Location label"
                  value={seriesForm.locationLabel}
                  onChange={(event) => updateSeriesField('locationLabel', event.target.value)}
                  className={styles.input}
                  placeholder="Zoom"
                />
              </div>
              <div>
                <label htmlFor="weekly-series-link" className={`${styles.label} mb-1 block`}>Optional link</label>
                <Input
                  id="weekly-series-link"
                  aria-label="Optional link"
                  value={seriesForm.link}
                  onChange={(event) => updateSeriesField('link', event.target.value)}
                  className={styles.input}
                  placeholder="https://zoom.us/..."
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="weekly-series-description" className={`${styles.label} mb-1 block`}>Description</label>
                <Textarea
                  id="weekly-series-description"
                  aria-label="Description"
                  value={seriesForm.description}
                  onChange={(event) => updateSeriesField('description', event.target.value)}
                  className={`${styles.input} min-h-[96px]`}
                />
              </div>
              <div className="space-y-3 md:col-span-2">
                {[
                  ['skipFifthOccurrence', `Skip 5th ${WEEKDAY_OPTIONS[seriesForm.dayOfWeek].label}s`],
                  ['skipHolidays', 'Skip listed holidays'],
                  ['skipMothersDay', "Skip Mother's Day"],
                  ['skipFathersDay', "Skip Father's Day"],
                ].map(([field, label]) => (
                  <label key={field} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={seriesForm[field]}
                      onChange={(event) => updateSeriesField(field, event.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-aqua focus:ring-aqua"
                    />
                    {label}
                  </label>
                ))}
              </div>
              <div className="md:col-span-2">
                <label htmlFor="weekly-series-skips" className={`${styles.label} mb-1 block`}>Also skip these dates</label>
                <Textarea
                  id="weekly-series-skips"
                  aria-label="Also skip these dates"
                  value={seriesForm.customSkipText}
                  onChange={(event) => updateSeriesField('customSkipText', event.target.value)}
                  className={`${styles.input} min-h-[96px]`}
                  placeholder={'2026-09-06\n2026-10-11, 2026-11-01'}
                />
                <p className={`${styles.subtleText} mt-2`}>Use YYYY-MM-DD, one per line or comma-separated.</p>
              </div>
            </div>
          )}

          <div role="status" aria-live="polite" aria-atomic="true">
            {seriesIssues.length > 0 ? (
              <div className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 dark:border-red-400/20 dark:bg-red-500/5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-700 dark:text-red-300">
                  Fix before previewing
                </p>
                <div className="mt-2 space-y-1 text-sm text-red-700 dark:text-red-200">
                  {seriesIssues.map((message) => <p key={message}>{message}</p>)}
                </div>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            {seriesPreview ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSeriesPreview(null);
                    setSeriesIssues([]);
                  }}
                  className={styles.outlineButton}
                >
                  Back to edit
                </Button>
                <Button type="button" variant="outline" onClick={closeSeriesDialog} className={styles.outlineButton}>
                  Cancel
                </Button>
                <Button type="button" onClick={confirmWeeklySeries} className="bg-aqua text-charcoal hover:bg-aqua-dark">
                  Add events to draft
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={closeSeriesDialog} className={styles.outlineButton}>
                  Cancel
                </Button>
                <Button type="button" onClick={previewWeeklySeries} className="bg-aqua text-charcoal hover:bg-aqua-dark">
                  Preview series
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={eventEditorOpen} onOpenChange={(open) => (open ? setEventEditorOpen(true) : closeEventEditor())}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{eventExists ? 'Edit event' : 'Add event'}</DialogTitle>
            <DialogDescription>
              Fill in the event details here, then save the Site Settings page when the list looks right.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className={`${styles.label} mb-1 block`}>Event title</label>
              <Input
                value={eventDraft.title}
                onChange={(e) => setEventDraft((current) => ({ ...current, title: e.target.value }))}
                className={styles.input}
                placeholder="Dad-Partners Webinar"
              />
            </div>
            <div>
              <label className={`${styles.label} mb-1 block`}>Date</label>
              <Input
                type="date"
                value={eventDraft.date}
                onChange={(e) => setEventDraft((current) => ({ ...current, date: e.target.value }))}
                className={styles.dateInput || styles.input}
              />
            </div>
            <div>
              <label className={`${styles.label} mb-1 block`}>Start time</label>
              <Input
                type="time"
                value={eventDraft.start_time}
                onChange={(e) => setEventDraft((current) => ({ ...current, start_time: e.target.value }))}
                className={styles.input}
              />
            </div>
            <div>
              <label className={`${styles.label} mb-1 block`}>End time</label>
              <Input
                type="time"
                value={eventDraft.end_time}
                onChange={(e) => setEventDraft((current) => ({ ...current, end_time: e.target.value }))}
                className={styles.input}
              />
              <p className={`${styles.subtleText} mt-2`}>Optional. Leave blank if the event only needs a start time.</p>
            </div>
            <div>
              <label className={`${styles.label} mb-1 block`}>Location label</label>
              <Input
                value={eventDraft.location_label}
                onChange={(e) => setEventDraft((current) => ({ ...current, location_label: e.target.value }))}
                className={styles.input}
                placeholder="Zoom"
              />
            </div>
            <div className="md:col-span-2">
              <label className={`${styles.label} mb-1 block`}>Optional link</label>
              <Input
                value={eventDraft.link}
                onChange={(e) => setEventDraft((current) => ({ ...current, link: e.target.value }))}
                className={styles.input}
                placeholder="https://zoom.us/..."
              />
            </div>
            <div className="md:col-span-2">
              <label className={`${styles.label} mb-1 block`}>Description</label>
              <Textarea
                value={eventDraft.description}
                onChange={(e) => setEventDraft((current) => ({ ...current, description: e.target.value }))}
                className={`${styles.input} min-h-[96px]`}
                placeholder="Short context for the event."
              />
            </div>
          </div>

          {eventDraftIssues.length > 0 ? (
            <div className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 dark:border-red-400/20 dark:bg-red-500/5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-700 dark:text-red-300">
                Fix before adding this event
              </p>
              <div className="mt-2 space-y-1 text-sm text-red-700 dark:text-red-200">
                {eventDraftIssues.map((issue) => (
                  <p key={issue}>{issue}</p>
                ))}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeEventEditor} className={styles.outlineButton}>
              Cancel
            </Button>
            <Button type="button" onClick={saveEventDraft} className="bg-aqua text-charcoal hover:bg-aqua-dark">
              {eventExists ? 'Update event' : 'Add event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(eventPendingDelete)} onOpenChange={(open) => (!open ? setEventPendingDelete(null) : null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete event?</DialogTitle>
            <DialogDescription>
              {`Remove ${eventPendingDelete?.title?.trim() || 'this event'} from draft settings? This only becomes permanent after you click Save Settings.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEventPendingDelete(null)} className={styles.outlineButton}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmRemoveUpcomingEvent} className="bg-red-600 text-white hover:bg-red-700">
              Delete event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpcomingEventsSection;
